import twitterText from 'twitter-text';
import { MastodonParser } from '~/composer/composer/lib/parsers/MastodonParser';
import { AttachmentTypes } from '~/constants';
import { EditorStateProxy } from '../EditorStateProxy/EditorStateProxy';
import type { Draft } from './Draft';

export const DraftMethods = {
  hasLinkAttachmentEnabled(draft): boolean {
    return draft.enabledAttachmentType === AttachmentTypes.LINK;
  },

  hasMediaAttachmentEnabled(draft): boolean {
    return draft.enabledAttachmentType === AttachmentTypes.MEDIA;
  },

  hasRetweetAttachmentEnabled(draft): boolean {
    return draft.enabledAttachmentType === AttachmentTypes.RETWEET;
  },

  hasLinkAttachment(draft): boolean {
    return DraftMethods.hasLinkAttachmentEnabled(draft) && draft.link !== null;
  },

  hasLinkAttachmentWithThumbnail(draft): boolean {
    return (
      DraftMethods.hasLinkAttachmentEnabled(draft) &&
      draft.link?.thumbnail !== null
    );
  },

  hasMediaAttachment(draft): boolean {
    return (
      (DraftMethods.hasMediaAttachmentEnabled(draft) &&
        draft.images?.length > 0) ||
      draft.video !== null ||
      draft.gif !== null
    );
  },

  hasRetweetAttachment(draft): boolean {
    return (
      DraftMethods.hasRetweetAttachmentEnabled(draft) && draft.retweet !== null
    );
  },

  hasAttachment(draft): boolean {
    return (
      DraftMethods.hasLinkAttachment(draft) ||
      DraftMethods.hasMediaAttachment(draft) ||
      DraftMethods.hasRetweetAttachment(draft)
    );
  },

  hasNoMediaAttached(draft): boolean {
    return (
      draft.images.length === 0 && draft.video === null && draft.gif === null
    );
  },

  hasVideoAttachment(draft): boolean {
    return (
      DraftMethods.hasMediaAttachmentEnabled(draft) && draft.video !== null
    );
  },

  hasImagesAttachment(draft): boolean {
    return (
      DraftMethods.hasMediaAttachmentEnabled(draft) && draft.images.length !== 0
    );
  },

  hasText(draft): boolean {
    if (draft.editorState) {
      return EditorStateProxy.hasText(draft.editorState);
    }

    return (
      draft.text !== null &&
      draft.text !== undefined &&
      draft.text.trim() !== ''
    );
  },

  hasUnshortenedUrls(draft): boolean {
    // draftjs clenaup: draft.unshortenedUrls is only used for DraftJS
    const hasUnshortenedUrlsInArray = draft.unshortenedUrls.length > 0;

    const hasUnshortenedUrlsInEditorState =
      !!draft.editorState &&
      EditorStateProxy.hasUnshortenedLinks(draft.editorState);

    return hasUnshortenedUrlsInArray || hasUnshortenedUrlsInEditorState;
  },

  /**
   * Returns the additional amount of characters that need to be
   * added to the character count under specific conditions.
   *
   * Some services require that the character count be adjusted
   * under specific conditions, eg: A post has an image or video attachment.
   *
   * These characters all relate to things outside of the text.
   */
  getAdditionalCharacterCount(draft: Draft, text: string): number {
    if (draft.service.isTwitter()) {
      const mockAttachmentLink = 'https://pbs.twimg.com/media/XXXXXX';
      const mockAttachmentText = ` ${mockAttachmentLink}`;

      if (
        draft.enabledAttachmentType === AttachmentTypes.MEDIA &&
        draft.video !== null &&
        draft.service.nativeVideoSizeLimit &&
        draft.service.nativeVideoDurationLimit
      ) {
        const videoSizeLessThanNativeLimit =
          draft.video.size < draft.service.nativeVideoSizeLimit;
        const videoDurationLessThanNativeLimit =
          draft.video.durationMs <
          draft.service.nativeVideoDurationLimit * 1000;

        const willVideoBeNativeOnTwitter =
          videoSizeLessThanNativeLimit && videoDurationLessThanNativeLimit;
        const willVideoLinkBeAddedToText = !willVideoBeNativeOnTwitter;

        if (willVideoLinkBeAddedToText) {
          return twitterText.getTweetLength(mockAttachmentText);
        }
      }
    }

    if (draft.service.isLinkedin()) {
      let charCount = 0;

      const urls = twitterText.extractUrls(text);
      const hasLinkAttached =
        draft.enabledAttachmentType === AttachmentTypes.LINK &&
        draft.link !== null;
      const isAttachedLinkInText =
        draft?.link && hasLinkAttached && urls.includes(draft.link.url);

      // Only add 24 chars for link attachment if the
      // attached link is not also found in the text
      if (hasLinkAttached && !isAttachedLinkInText) charCount += 24;

      // Add 24 chars for images and videos
      if (draft.enabledAttachmentType === AttachmentTypes.MEDIA) {
        if (draft.images?.length > 0) charCount += 24;
        if (draft.video !== null) charCount += 24;
      }

      return charCount;
    }

    return 0;
  },

  /**
   * Returns the character count for the given text.
   */
  getTextCharacterCount(draft: Draft, text: string): number {
    if (draft.service.isTwitter()) {
      return twitterText.getTweetLength(text);
    }

    if (draft.service.isInstagram()) {
      const charCount = (text && text.length) || 0;
      let lineBreakLength = 0;

      const lineBreakMatch = text?.match(/\n/g);
      if (text && lineBreakMatch) {
        lineBreakLength = lineBreakMatch.length;
      }
      return charCount + lineBreakLength;
    }

    if (draft.service.isLinkedin()) {
      let charCount = (text && text.length) || 0;

      const urls = twitterText.extractUrls(text);

      // If there are urls in the text, subtract the url length
      // from the charCount and add back 24 chars for each url
      if (urls.length > 0) {
        let totalUrlLength = 0;

        urls.forEach((url) => {
          totalUrlLength += url.length;
        });

        const additionalCharCount = 24;
        charCount -= totalUrlLength;
        charCount += urls.length * additionalCharCount;
      }

      return charCount;
    }

    if (draft.service.isMastodon()) {
      return MastodonParser.getCharCount(text);
    }

    return (text && text.length) || 0;
  },

  /**
   * Returns the text character count plus any additional characters
   * that need to be added under specific conditions;
   * eg: A post has an image or video attachment.
   */
  getFullCharacterCount(draft: Draft, text: string): number | undefined {
    return (
      DraftMethods.getTextCharacterCount(draft, text) +
      DraftMethods.getAdditionalCharacterCount(draft, text)
    );
  },

  getJoinedThreadsText(draft: Draft): string | undefined {
    return draft?.thread
      ?.reduce<string[]>((threadedDraftsText, threadedDraft) => {
        threadedDraftsText.push(threadedDraft.text);
        if (
          threadedDraft?.retweet?.text &&
          threadedDraft.retweet.text.trim() !== ''
        ) {
          threadedDraftsText.push(threadedDraft.retweet.text);
        }
        return threadedDraftsText;
      }, [])
      .join('\n');
  },

  getUploaderId(draft: Draft): string {
    const { id, updateType } = draft;

    return `uploader-${id}-${updateType}`;
  },
};
